"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSidebar } from "@/contexts/SidebarContext";
import { cn } from "@/lib/utils";
import {
    LayoutDashboard,
    Code,
    Smartphone,
    Palette,
    Film,
    Mic,
    ChevronLeft,
    ChevronRight,
    LogOut,
    Settings,
    X
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { useRouter } from "next/navigation";

export default function GlobalSidebar() {
    const pathname = usePathname();
    const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen } = useSidebar();
    const { logout } = useAuthStore();
    const router = useRouter();

    // Don't show sidebar on auth pages or public pages
    const isPublic = [
        "/auth/login",
        "/auth/register",
        "/auth/forgot-password",
        "/",
        "/public/verify-email"
    ].includes(pathname || "") || pathname?.startsWith("/public");

    if (isPublic) return null;

    const navItems = [
        { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
        { name: "Vibe Coding", href: "/vibe-coding", icon: Code },
        { name: "Vibe Mobile", href: "/vibe-mobile", icon: Smartphone },
        { name: "Vibe Designer", href: "/vibe-designer", icon: Palette },
        { name: "Vibe Motion", href: "/vibe-motion", icon: Film },
        { name: "Vibe Voice", href: "/vibe-voice", icon: Mic },
    ];

    const handleLogout = async () => {
        setIsMobileOpen(false);
        await logout();
        router.push("/auth/login");
    };

    const handleNavClick = () => {
        // Close mobile sidebar when navigating
        setIsMobileOpen(false);
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300"
                    onClick={() => setIsMobileOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={cn(
                    "fixed top-0 left-0 z-50 h-screen bg-slate-900 border-r border-slate-800 transition-all duration-300 ease-in-out flex flex-col",
                    // Width: expanded on mobile when open, responsive on desktop
                    isMobileOpen ? "w-72" : (isCollapsed ? "w-16" : "w-64"),
                    // Transform: slide in/out on mobile
                    isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
                    // Shadow on mobile
                    isMobileOpen && "shadow-2xl"
                )}
            >
                {/* Header */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
                    {/* Logo - always show on mobile when open */}
                    {(!isCollapsed || isMobileOpen) && (
                        <div className="font-bold text-xl tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">
                            Vibe Coder
                        </div>
                    )}

                    {/* Mobile Close Button */}
                    <button
                        onClick={() => setIsMobileOpen(false)}
                        className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors md:hidden"
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>

                    {/* Desktop Collapse Button */}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors hidden md:block"
                        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
                    {navItems.map((item) => {
                        const isActive = pathname?.startsWith(item.href);
                        return (
                            <Link
                                key={item.href}
                                href={item.href}
                                onClick={handleNavClick}
                                className={cn(
                                    "flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group relative",
                                    isActive
                                        ? "bg-indigo-500/10 text-indigo-400"
                                        : "text-slate-400 hover:text-white hover:bg-slate-800"
                                )}
                                title={isCollapsed && !isMobileOpen ? item.name : undefined}
                            >
                                <item.icon size={22} className={cn("min-w-[22px] shrink-0", isActive && "text-indigo-400")} />
                                {/* Show label when expanded OR on mobile */}
                                {(!isCollapsed || isMobileOpen) && (
                                    <span className="font-medium truncate">{item.name}</span>
                                )}

                                {/* Active Indicator */}
                                {isActive && (
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-indigo-500 rounded-r-full" />
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Footer */}
                <div className="p-3 border-t border-slate-800 space-y-1">
                    {/* Settings */}
                    <button
                        className={cn(
                            "flex items-center gap-3 px-3 py-3 rounded-xl w-full transition-all duration-200 text-slate-400 hover:text-white hover:bg-slate-800"
                        )}
                        onClick={() => {
                            setIsMobileOpen(false);
                            router.push('/settings');
                        }}
                    >
                        <Settings size={22} className="min-w-[22px] shrink-0" />
                        {(!isCollapsed || isMobileOpen) && <span className="font-medium">Settings</span>}
                    </button>

                    {/* Logout */}
                    <button
                        onClick={handleLogout}
                        className={cn(
                            "flex items-center gap-3 px-3 py-3 rounded-xl w-full transition-all duration-200 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        )}
                    >
                        <LogOut size={22} className="min-w-[22px] shrink-0" />
                        {(!isCollapsed || isMobileOpen) && <span className="font-medium">Logout</span>}
                    </button>
                </div>
            </aside>
        </>
    );
}
